import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { AbilityContext } from '../Can';
import defineAbilityFor from '../../AppAbility';
import authService, { AuthenticationResultStatus } from './AuthorizeService';
import { LoginActions, QueryParameterNames, ApplicationPaths } from './ApiAuthorizationConstants';

// Resolve a candidate URL to a same-origin path-rooted string, or null if it
// would escape the origin or fails to parse. Uses the URL constructor with a
// fixed base so the result is always anchored to window.location.origin —
// CodeQL recognizes this pattern as a sanitizer for url-redirection / xss sinks.
const sanitizeReturnUrl = (rawUrl) => {
    if (typeof rawUrl !== 'string' || rawUrl === '') return null;
    try {
        const parsed = new URL(rawUrl, window.location.origin);
        if (parsed.origin !== window.location.origin) return null;
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return null;
    }
};

const getReturnUrl = (state) => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);

    const fromState = sanitizeReturnUrl(state?.returnUrl);
    if (fromState) return fromState;

    const sanitizedQuery = sanitizeReturnUrl(fromQuery);
    if (sanitizedQuery) return sanitizedQuery;

    const isAuthPath = window.location.pathname.startsWith(`${ApplicationPaths.ApiAuthorizationPrefix}/`);
    if (isAuthPath) return '/';
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const redirectToApiAuthorizationPath = (apiAuthorizationPath) => {
    const redirectUrl = `${window.location.origin}${apiAuthorizationPath}`;
    // It's important that we do a replace here so that when the user hits the back arrow on the
    // browser he gets sent back to where it was on the app instead of to an endpoint on this
    // component.
    window.location.replace(redirectUrl);
};

const renderLogin = (message) => (
    <div className="login-message container">
        <h5>
            <FontAwesomeIcon
                icon={faCircleNotch}
                className="fa-spin mr-2"
                size="lg"
            />
            {message}
        </h5>
    </div>
);

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to authenticate
// a user can simply perform a redirect to this component with a returnUrl query parameter and
// let the component perform the login and return back to the return url.
class LoginInner extends Component {
    static contextType = AbilityContext;

    constructor(props) {
        super(props);

        this.state = {
            message: undefined,
        };
    }

    componentDidMount() {
        const { action } = this.props;
        switch (action) {
        case LoginActions.Login:
            this.login(getReturnUrl());
            break;
        case LoginActions.LoginCallback:
            this.processLoginCallback();
            break;
        case LoginActions.LoginFailed: {
            const params = new URLSearchParams(window.location.search);
            const error = params.get(QueryParameterNames.Message);
            this.setState({ message: error });
            break;
        }
        case LoginActions.Profile:
            redirectToApiAuthorizationPath(ApplicationPaths.IdentityManagePath);
            break;
        case LoginActions.Register:
            redirectToApiAuthorizationPath(`${ApplicationPaths.IdentityRegisterPath}?${QueryParameterNames.ReturnUrl}=${encodeURI(ApplicationPaths.Login)}`);
            break;
        default:
            throw new Error(`Invalid action '${action}'`);
        }
    }

    async login(returnUrl) {
        const state = { returnUrl };
        const result = await authService.signIn(state);

        switch (result.status) {
        case AuthenticationResultStatus.Redirect:
            break;
        case AuthenticationResultStatus.Success:
            await this.updateUserPermissions();

            break;
        case AuthenticationResultStatus.Fail:
            this.setState({ message: result.message });
            break;
        default:
            throw new Error(`Invalid status result ${result.status}.`);
        }
    }

    async processLoginCallback() {
        const url = window.location.href;
        const result = await authService.completeSignIn(url);
        switch (result?.status) {
        case AuthenticationResultStatus.Redirect:
            // completeSignIn falls back to a redirect when signinCallback throws (state
            // already consumed, cross-origin storage, etc.). Navigation is in flight.
            break;
        case AuthenticationResultStatus.Success:
            window.location.replace(getReturnUrl(result.state));
            break;
        case AuthenticationResultStatus.Fail:
            this.setState({ message: result.message });
            break;
        default:
            throw new Error(`Invalid authentication result status '${result.status}'.`);
        }
    }

    async updateUserPermissions() {
        const ability = this.context;
        const rules = await defineAbilityFor();
        ability.update(rules);
    }

    render() {
        const { action, t } = this.props;
        const { message } = this.state;

        if (message) {
            return <div>{message}</div>;
        }
        switch (action) {
        case LoginActions.Login:
            return renderLogin(t('auth:checkingCredentials'));
        case LoginActions.LoginCallback:
            return renderLogin(t('auth:signingIn'));
        case LoginActions.Profile:
        case LoginActions.Register:
            return (<div />);
        default:
            throw new Error(`Invalid action '${action}'`);
        }
    }
}

export const Login = withTranslation()(LoginInner);
